// ═══════════════════════════════════════════════════════════════
// ProductDetailModal — Modal partagé pour afficher un produit partenaire
//
// Utilisé dans :
// - CatalogScreen (produits catalogue)
// - LooksScreen (suggestions dans les tenues)
// - WardrobeScreen (Mes Looks + Suggestions)
// - TravelWardrobeScreen (suggestions voyage)
// - CreateLookModal (suggestions IA étapes)
//
// Flow :
//   1. User tap un produit → parent fait setSelected(product)
//   2. Ce modal s'ouvre avec image, tailles, prix, etc
//   3. Bouton "Voir sur [partenaire]" → ouvre le lien affilié
//   4. Bouton "J'ai acheté → Ajouter au dressing" → callback onBought(product)
//      → parent doit alors ouvrir PurchaseConfirmModal
// ═══════════════════════════════════════════════════════════════

import React from 'react';
import {
  View, Text, Image, TouchableOpacity, ScrollView, Platform, StatusBar, BackHandler, useWindowDimensions, Pressable,
} from 'react-native';
import AppModal from './AppModal';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { displayName } from '../utils/displayName';
import AIMetadataDetails from './AIMetadataDetails';
import { displayProductReward } from '../services/CashbackDisplayService';
import { rootNavigate } from '../utils/rootNavigation';
import { useAuth } from '../context/AuthContext';
import { showAppAlert } from './AppDialog';
import { useI18n } from '../i18n';

// Info des partenaires pour l'affichage (nom, couleur, logo)
export const PARTNER_INFO = {
  zalando:  { name: 'Zalando',            color: '#FF6900', logo: 'Z'  },
  asos:     { name: 'ASOS',               color: '#111111', logo: 'A'  },
  hm:       { name: 'H&M',                color: '#E50010', logo: 'HM' },
  amazon:   { name: 'Amazon',             color: '#FF9900', logo: 'AM' },
  zara:     { name: 'Zara',               color: '#000000', logo: 'ZR' },
  iqueens:  { name: 'iQueens',            color: '#2A7D4F', logo: 'IQ' },
  cashmere: { name: 'Cashmere Boutique',  color: '#8B6F4E', logo: 'CB' },
  tous:     { name: 'TOUS',               color: '#D4AF37', logo: 'T'  },
  mango:    { name: 'Mango',              color: '#E50010', logo: 'MG' },
  rakuten:  { name: 'Rakuten',            color: '#BF0000', logo: 'R'  },
  // v1.12.298 — Suit Negozi via Rakuten Advertising (MID 53537)
  // shortLabel = "SUIT" pour le tag badge en haut, name = "Suit Negozi" pour le bouton "Voir sur..."
  suit_negozi: { name: 'Suit Negozi',     color: '#1A1A1A', logo: 'SUIT', shortLabel: 'SUIT' },
  // v1.12.322 — Ann Demeulemeester via Rakuten Advertising (MID 53588)
  ann_demeulemeester: { name: 'Ann Demeulemeester', color: '#1A1A1A', logo: 'ANN', shortLabel: 'ANN' },
  // v1.12.336 — COUTR (USA luxe multi-marques, prix USD)
  coutr: { name: 'COUTR', color: '#1A1A1A', logo: 'COUTR', shortLabel: 'COUTR' },
};

export default function ProductDetailModal({ product, onClose, onBought, userId, screen }) {
  if (!product) return null;

  // v1.12.144 — Tier user pour cashback variable (free 5%, basic 10%, premium 15%, gold 25%)
  const { user: authUser } = useAuth();
  const userTier = (authUser?.tier || 'free').toLowerCase();
  const { lang } = useI18n();
  const en = lang === 'en';

  const insets = useSafeAreaInsets();
  // ═══ FIX 2026-04 : hauteur image proportionnelle à l'écran ═══
  // Avant : height fixe à 380px → tronquait l'image sur tablette / grand écran
  //   et laissait beaucoup d'espace blanc en bas (cf. screenshot tablette).
  // Maintenant : ratio 4:5 portrait (typique pour vêtements) basé sur la
  //   largeur de la fenêtre, plafonné à 65% de la hauteur pour garder de la
  //   place aux infos produit en bas. Combiné avec resizeMode='contain'
  //   ci-dessous, la photo entière est toujours visible.
  const { width: winW, height: winH } = useWindowDimensions();
  const imageHeight = Math.min(winW * 1.25, winH * 0.65);
  // ═══ FIX padding header Android ═══
  // Sur Android, la barre de statut est translucide donc il faut ajouter
  // StatusBar.currentHeight en plus des insets (qui peuvent être 0)
  const topPadding = Platform.OS === 'android'
    ? Math.max(insets.top, StatusBar.currentHeight || 24) + 8
    : Math.max(insets.top, 8);
  // ═══ FIX padding footer Android ═══
  // Barre de navigation système Android (3 boutons ║ ○ ◁) mange ~48px
  // qui recouvrent les boutons du footer.
  const bottomPadding = Math.max(insets.bottom, 12) + 16;

  const [selectedSize, setSelectedSize] = React.useState(product.sizes?.[0] || 'Unique');
  const [imgDead,      setImgDead]      = React.useState(false);
  const [imgProxied,   setImgProxied]   = React.useState(false);
  const [isFav,        setIsFav]        = React.useState(false);
  const [favBusy,      setFavBusy]      = React.useState(false);
  const [showRewardSteps, setShowRewardSteps] = React.useState(false);
  // v1.12.545 — Lightbox pinch-to-zoom plein écran sur l'image principale
  const [zoomVisible,  setZoomVisible]  = React.useState(false);

  // Charger l'état favori initial
  React.useEffect(() => {
    if (!product?.id) return;
    (async () => {
      try {
        const { isFavorite } = require('../services/FavoritesService');
        const fav = await isFavorite(product.id);
        setIsFav(!!fav);
      } catch {}
    })();
    // v1.12.682 — GA4 : ouverture page détail article partenaire
    try {
      const { trackEvent } = require('../services/AnalyticsService');
      trackEvent('view_item', {
        product_id: String(product.id).slice(0, 100),
        partner: product.partner || 'unknown',
        brand: product.brand || null,
        category: product.category || null,
        price: typeof product.price === 'number' ? product.price : parseFloat(product.price) || 0,
        currency: product.currency || 'EUR',
        screen: screen || 'unknown',
      });
    } catch {}
  }, [product?.id]);

  const onToggleFavorite = async () => {
    if (favBusy) return;
    setFavBusy(true);
    try {
      const { toggleFavorite } = require('../services/FavoritesService');
      const nowFav = await toggleFavorite(product);
      setIsFav(nowFav);
    } catch(e) {
      // silent
    } finally {
      setFavBusy(false);
    }
  };

  const info    = PARTNER_INFO[product.partner] || { name: product.partner || 'Partenaire', color: '#333', logo: '?' };
  const hasUrl  = !!(product.affiliateUrl || product.originalUrl || product.url);

  // ═══ Collection d'images : rassemble TOUTES les images dispo du produit ═══
  // Source : product.images (array de l'API partenaire), sinon singles fields.
  // Les images restent pointées vers le CDN partenaire - AUCUN upload S3.
  // Si 1 seule image, tentative de générer des variantes via pattern CDN connu
  const allImages = React.useMemo(() => {
    const set = new Set();
    const add = (url) => {
      if (url && typeof url === 'string' && /^https?:\/\//.test(url)) {
        set.add(url);
      }
    };
    if (Array.isArray(product.images)) product.images.forEach(add);
    add(product.image);
    add(product.image_url);
    add(product.img);
    add(product.photo);
    if (Array.isArray(product.photos)) product.photos.forEach(add);
    if (Array.isArray(product.image_urls)) product.image_urls.forEach(add);

    let arr = Array.from(set);

    // ═══ Enrichissement : si 1 seule image, tenter variantes CDN connus ═══
    if (arr.length === 1) {
      const url = arr[0];

      // Pattern Asos : /products/.../image-1.jpg → tester -2, -3, -4
      const asosMatch = url.match(/^(.+\/[^/]+)-(\d+)(\.\w+.*?)$/);
      if (asosMatch && /asos/i.test(url)) {
        const [, base, num, ext] = asosMatch;
        const n = parseInt(num, 10);
        for (let i = 1; i <= 4; i++) {
          if (i !== n) arr.push(`${base}-${i}${ext}`);
        }
      }

      // Pattern Zalando : URL contient imwidth=500 → on peut générer différentes tailles
      // MAIS ça affiche 4x la même image, donc on skip.
      // Pour avoir de vraies images différentes il faudrait l'API Zalando.

      // Pattern H&M : URLs contiennent _01.jpg, _02.jpg, _03.jpg
      const hmMatch = url.match(/^(.+)_(\d{2})(\.\w+)$/);
      if (hmMatch && /h.?m|hm-|hennes/i.test(url)) {
        const [, base, num, ext] = hmMatch;
        const n = parseInt(num, 10);
        for (let i = 1; i <= 4; i++) {
          const padded = String(i).padStart(2, '0');
          if (i !== n) arr.push(`${base}_${padded}${ext}`);
        }
      }
    }

    return arr;
  }, [product]);

  // Index de l'image actuellement affichée (galerie)
  const [currentImgIdx, setCurrentImgIdx] = React.useState(0);
  // Set des vignettes qui ont échoué au chargement (retirées du carousel)
  const [deadThumbs, setDeadThumbs] = React.useState(new Set());
  // Reset à 0 quand le produit change
  React.useEffect(() => { setCurrentImgIdx(0); setImgDead(false); setImgProxied(false); setDeadThumbs(new Set()); }, [product?.id]);

  // Images valides (sans celles qui ont échoué)
  const validImages = React.useMemo(
    () => allImages.filter((_, i) => !deadThumbs.has(i)),
    [allImages, deadThumbs]
  );

  const rawImage = allImages[currentImgIdx] || null;

  // Gestion d'image avec proxy wsrv.nl pour Zara, Cashmere et TOUS (CORS / blocage)
  const isZara     = rawImage && rawImage.includes('static.zara.net');
  const isCashmere = rawImage && rawImage.includes('cashmereboutique.com');
  // ═══ v1.9.30 : Auto-proxy TOUS (static.tous.com lent depuis certains réseaux) ═══
  const isTous     = rawImage && rawImage.includes('static.tous.com');
  const isImgProxy = rawImage && rawImage.includes('/img-proxy');
  const imgSrc    = !rawImage || imgDead ? null
                  : isImgProxy   ? rawImage
                  : isCashmere && !imgProxied ? rawImage
                  : isZara       ? 'https://wsrv.nl/?url=' + encodeURIComponent(rawImage) + '&w=800'
                  : isTous       ? 'https://wsrv.nl/?url=' + encodeURIComponent(rawImage) + '&w=800'
                  : isCashmere   ? 'https://wsrv.nl/?url=' + encodeURIComponent(rawImage) + '&w=800'
                  : imgProxied   ? 'https://wsrv.nl/?url=' + encodeURIComponent(rawImage) + '&w=800'
                  : rawImage;

  // ═══ Android : gérer le bouton back hardware ═══
  // Quand l'user appuie sur le bouton back physique / gesture, on ferme la modal
  // (= retour à l'écran précédent), au lieu de quitter l'app
  React.useEffect(() => {
    const onBack = () => {
      if (typeof onClose === 'function') {
        onClose();
        return true; // on a géré l'event
      }
      return false;
    };
    const sub = BackHandler.addEventListener('hardwareBackPress', onBack);
    return () => sub.remove();
  }, [onClose]);

  const openInBrowser = () => {
    try {
      const { openProductForPurchase } = require('../services/PurchaseTrackingService');
      openProductForPurchase(product, userId, { screen: screen || 'unknown' });
    } catch(e) {
      // silent
    }
  };

  return (
    <AppModal
      visible
      animationType="slide"
      presentationStyle={'overFullScreen'}
      onRequestClose={onClose}
      transparent={false}
    >
      <SafeAreaView style={{ flex: 1, backgroundColor: '#fff' }} edges={['left', 'right']}>
        {/* Header : back button + partner badge + favorite heart */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 16, paddingTop: topPadding, paddingBottom: 12 }}>
          <TouchableOpacity onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={{ width: 36, height: 36, alignItems: 'center', justifyContent: 'center', backgroundColor: '#F5F5F5', borderRadius: 18 }}>
            <Text style={{ fontSize: 20, fontWeight: '600', color: '#333', lineHeight: 22 }}>←</Text>
          </TouchableOpacity>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            {/* Bouton Favoris */}
            <TouchableOpacity
              onPress={onToggleFavorite}
              disabled={favBusy}
              style={{
                width: 36, height: 36, alignItems: 'center', justifyContent: 'center',
                backgroundColor: isFav ? '#FFEBEE' : '#F5F5F5',
                borderRadius: 18,
                borderWidth: 1.5,
                borderColor: isFav ? '#E53935' : 'transparent',
              }}
              activeOpacity={0.7}
            >
              <Text style={{ fontSize: 16 }}>{isFav ? '❤️' : '🤍'}</Text>
            </TouchableOpacity>
            {/* Badge partenaire — v1.12.298 : tag court (info.shortLabel ou logo) pour les noms longs */}
            <View style={{ paddingHorizontal: 14, paddingVertical: 6, borderRadius: 20, backgroundColor: info.color }}>
              <Text style={{ fontSize: 11, fontWeight: '700', color: '#fff', letterSpacing: 1 }}>{info.shortLabel || info.name}</Text>
            </View>
          </View>
        </View>

        <ScrollView showsVerticalScrollIndicator={false}>
          {/* Image principale — v1.12.545 : tap pour zoom plein écran */}
          <View style={{ width: '100%', height: imageHeight, backgroundColor: '#FAFAFA' }}>
            {!imgSrc
              ? <View style={{ flex: 1, backgroundColor: '#F5F5F5', alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={{ fontSize: 64 }}>{product.emoji || '👗'}</Text>
                </View>
              : <TouchableOpacity activeOpacity={0.95} onPress={() => setZoomVisible(true)} style={{ width: '100%', height: '100%' }}>
                  <Image source={{ uri: imgSrc }}
                    style={{ width: '100%', height: '100%', resizeMode: 'contain' }}
                    onError={(e) => {
                      if (!imgProxied) {
                        setImgProxied(true);
                      } else {
                        setImgDead(true);
                      }
                    }} />
                  <View style={{
                    position: 'absolute', bottom: 10, right: 10,
                    backgroundColor: 'rgba(0,0,0,0.55)',
                    paddingHorizontal: 9, paddingVertical: 4, borderRadius: 12,
                    flexDirection: 'row', alignItems: 'center', gap: 4,
                  }}>
                    <Text style={{ fontSize: 11, color: '#FFF' }}>🔍</Text>
                    <Text style={{ fontSize: 10, color: '#FFF', fontWeight: '600', letterSpacing: 0.3 }}>{en ? 'Zoom' : 'Zoom'}</Text>
                  </View>
                </TouchableOpacity>
            }
            {product.discount > 0 && (
              <View style={{ position: 'absolute', top: 12, left: 12, backgroundColor: '#E53935', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 4 }}>
                <Text style={{ fontSize: 11, fontWeight: '700', color: '#fff' }}>-{product.discount}%</Text>
              </View>
            )}
            {/* Dots d'indication (si plusieurs images valides) */}
            {validImages.length > 1 && (
              <View style={{
                position: 'absolute', bottom: 12, left: 0, right: 0,
                flexDirection: 'row', justifyContent: 'center', gap: 6,
              }}>
                {allImages.map((_, i) => {
                  if (deadThumbs.has(i)) return null;
                  return (
                    <View key={i} style={{
                      width: i === currentImgIdx ? 18 : 6,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: i === currentImgIdx ? '#FFF' : 'rgba(255,255,255,0.5)',
                    }} />
                  );
                })}
              </View>
            )}
            {/* Compteur (ex: 1/4) */}
            {validImages.length > 1 && (
              <View style={{
                position: 'absolute', top: 12, right: 12,
                paddingHorizontal: 8, paddingVertical: 4,
                backgroundColor: 'rgba(0,0,0,0.55)',
                borderRadius: 10,
              }}>
                <Text style={{ color: '#FFF', fontSize: 11, fontWeight: '600' }}>
                  {validImages.findIndex((_, vi) => allImages.indexOf(validImages[vi]) === currentImgIdx) + 1 || (currentImgIdx + 1)} / {validImages.length}
                </Text>
              </View>
            )}
          </View>

          {/* ═══ Vignettes des autres images (si plusieurs valides) ═══ */}
          {/* Scrollables horizontalement, clic pour changer l'image principale */}
          {/* Les vignettes qui échouent au chargement sont retirées automatiquement */}
          {validImages.length > 1 && (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 12, gap: 8 }}
              style={{ backgroundColor: '#FAFAFA', borderBottomWidth: 1, borderBottomColor: '#EEE' }}
            >
              {allImages.map((img, i) => {
                if (deadThumbs.has(i)) return null; // ne pas afficher les vignettes mortes
                const isZ = img.includes('static.zara.net');
                const isC = img.includes('cashmereboutique.com');
                // ═══ v1.9.30 : Auto-proxy TOUS dans les thumbnails ═══
                const isT = img.includes('static.tous.com');
                const thumbUri = isZ || isC || isT
                  ? 'https://wsrv.nl/?url=' + encodeURIComponent(img) + '&w=200'
                  : img;
                return (
                  <TouchableOpacity
                    key={i}
                    onPress={() => { setCurrentImgIdx(i); setImgDead(false); setImgProxied(false); }}
                    activeOpacity={0.7}
                    style={{
                      width: 64, height: 80,
                      borderRadius: 6,
                      borderWidth: 2,
                      borderColor: i === currentImgIdx ? '#111' : 'transparent',
                      overflow: 'hidden',
                    }}
                  >
                    <Image
                      source={{ uri: thumbUri }}
                      style={{ width: '100%', height: '100%', resizeMode: 'cover' }}
                      onError={() => {
                        // Vignette 404 (ex: variante générée qui n'existe pas) → la retirer
                        setDeadThumbs(prev => new Set([...prev, i]));
                      }}
                    />
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          )}

          {/* Infos */}
          <View style={{ padding: 20, gap: 8 }}>
            {product.brand ? (
              <Text style={{ fontSize: 10, letterSpacing: 2, color: '#999', textTransform: 'uppercase' }}>{product.brand}</Text>
            ) : null}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <Text style={{ flex: 1, fontSize: 18, fontWeight: '400', color: '#111', lineHeight: 24 }}>{displayName(product, lang)}</Text>
              <View style={{ alignItems: 'flex-end' }}>
                {/* v1.12.336 — affichage devise par produit (USD pour COUTR, EUR sinon) */}
                {/* v1.12.531 — Skip si prix absent (articles dressing sans prix) → évite "undefined €" */}
                {(() => {
                  const priceNum = Number(product.price);
                  if (!product.price || isNaN(priceNum) || priceNum <= 0) return null;
                  const isUsd = product.partner === 'coutr' || product.currency === 'USD';
                  const cur = isUsd ? '$' : '€';
                  const origNum = Number(product.originalPrice);
                  const hasOrig = !isNaN(origNum) && origNum > priceNum + 0.5;
                  return (
                    <>
                      <Text style={{ fontSize: 20, fontWeight: '700', color: '#111' }}>
                        {isUsd ? `${cur}${priceNum}` : `${priceNum} ${cur}`}
                      </Text>
                      {hasOrig && (
                        <Text style={{ fontSize: 12, color: '#999', textDecorationLine: 'line-through' }}>
                          {isUsd ? `${cur}${origNum}` : `${origNum} ${cur}`}
                        </Text>
                      )}
                    </>
                  );
                })()}
              </View>
            </View>
            {product.color ? <Text style={{ fontSize: 12, color: '#666' }}>{en ? 'Color' : 'Couleur'} : {product.color}</Text> : null}
            {(() => {
              // v1.12.146 — Skip rendering raw JSON descriptions (TOUS/iqueens stockent
              // {"buy_url":...,"long_description":"..."} dans description). Si JSON, on
              // extrait une éventuelle description lisible, sinon on n'affiche rien.
              const raw = product.description;
              if (!raw || typeof raw !== 'string') return null;
              const trimmed = raw.trim();
              if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                try {
                  const parsed = JSON.parse(trimmed);
                  const readable = parsed.long_description || parsed.short_description || parsed.text || parsed.description;
                  if (readable && typeof readable === 'string' && readable.length > 5 && !readable.startsWith('{')) {
                    return <Text style={{ fontSize: 13, color: '#555', lineHeight: 20 }}>{readable}</Text>;
                  }
                } catch {}
                return null; // JSON sans champ lisible -> ne rien afficher
              }
              return <Text style={{ fontSize: 13, color: '#555', lineHeight: 20 }}>{raw}</Text>;
            })()}

            {/* ═══ RÉCOMPENSE (v1.12.317) — uniquement le montant final, pas de détail calcul ═══ */}
            {(() => {
              const reward = displayProductReward(product, userTier, lang);
              if (!reward.amount || reward.amount <= 0) return null;
              return (
                <View style={{
                  marginTop: 4,
                  backgroundColor: '#E8F5E9',
                  borderWidth: 1.5,
                  borderColor: '#4CAF50',
                  borderRadius: 12,
                  paddingHorizontal: 14,
                  paddingVertical: 12,
                  gap: 10,
                }}>
                  {/* Header : icône + montant net user */}
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    <View style={{
                      width: 38, height: 38, borderRadius: 19,
                      backgroundColor: '#4CAF50',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Text style={{ fontSize: 18 }}>💰</Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 10, fontWeight: '700', color: '#1B5E20', letterSpacing: 1 }}>
                        {reward.label.toUpperCase()}
                      </Text>
                      <Text style={{ fontSize: 18, fontWeight: '700', color: '#1B5E20', marginTop: 2 }}>
                        {reward.primary.replace('💰 ', '')}
                      </Text>
                    </View>
                  </View>

                  {/* v1.12.317 — détail du calcul retiré (afficher uniquement le montant final) */}

                  {/* Comment obtenir la récompense — collapsé par défaut (v1.12.893) */}
                  <View style={{
                    paddingTop: 10,
                    borderTopWidth: 1,
                    borderTopColor: '#A5D6A7',
                    gap: 6,
                  }}>
                    <TouchableOpacity
                      onPress={() => setShowRewardSteps(v => !v)}
                      activeOpacity={0.7}
                      style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <Text style={{ fontSize: 10, fontWeight: '700', color: '#1B5E20', letterSpacing: 0.8 }}>
                        {en ? 'HOW TO GET YOUR REWARD' : 'COMMENT OBTENIR VOTRE RÉCOMPENSE'}
                      </Text>
                      <Text style={{ fontSize: 11, fontWeight: '600', color: '#1B5E20' }}>
                        {showRewardSteps ? (en ? 'Hide ▲' : 'Masquer ▲') : (en ? 'See more ▼' : 'Voir plus ▼')}
                      </Text>
                    </TouchableOpacity>
                    {showRewardSteps && (
                      <View style={{ gap: 6, marginTop: 4 }}>
                        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
                          <Text style={{ fontSize: 11, color: '#1B5E20', fontWeight: '700' }}>1.</Text>
                          <Text style={{ flex: 1, fontSize: 11, color: '#2E7D32', lineHeight: 15 }}>
                            {en ? 'Tap "View product" to go to the partner.' : 'Cliquez sur "Voir le produit" pour passer chez le partenaire.'}
                          </Text>
                        </View>
                        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
                          <Text style={{ fontSize: 11, color: '#1B5E20', fontWeight: '700' }}>2.</Text>
                          <Text style={{ flex: 1, fontSize: 11, color: '#2E7D32', lineHeight: 15 }}>
                            {en
                              ? <>Complete your purchase <Text style={{ fontWeight: '700' }}>without closing</Text> the session opened by Mytenue.</>
                              : <>Finalisez votre achat <Text style={{ fontWeight: '700' }}>sans fermer</Text> la session ouverte par MyTenue.</>}
                          </Text>
                        </View>
                        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
                          <Text style={{ fontSize: 11, color: '#1B5E20', fontWeight: '700' }}>3.</Text>
                          <Text style={{ flex: 1, fontSize: 11, color: '#2E7D32', lineHeight: 15 }}>
                            {en
                              ? <>Your reward appears in <Text style={{ fontWeight: '700' }}>My earnings</Text> within <Text style={{ fontWeight: '700' }}>1 to 2 days</Text> (tracking visible).</>
                              : <>Votre récompense s'affiche dans <Text style={{ fontWeight: '700' }}>Mes gains</Text> sous <Text style={{ fontWeight: '700' }}>1 à 2 jours</Text> (suivi visible).</>}
                          </Text>
                        </View>
                        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
                          <Text style={{ fontSize: 11, color: '#1B5E20', fontWeight: '700' }}>4.</Text>
                          <Text style={{ flex: 1, fontSize: 11, color: '#2E7D32', lineHeight: 15 }}>
                            {en
                              ? <>Final validation by the partner within <Text style={{ fontWeight: '700' }}>30 to 60 days</Text> (once the return window has passed).</>
                              : <>Validation finale par le partenaire sous <Text style={{ fontWeight: '700' }}>30 à 60 jours</Text> (le temps que le retour produit ne soit plus possible).</>}
                          </Text>
                        </View>
                      </View>
                    )}
                  </View>

                  {/* v1.12.318 — Encourage l'abonnement Gold : si tier user != gold,
                       afficher le montant qu'il toucherait en Gold + prix abonnement.
                     v1.12.321 — + lien "Voir les abonnements" qui ouvre l'onglet Profil. */}
                  {userTier !== 'gold' && (() => {
                    const goldReward = displayProductReward(product, 'gold', lang);
                    if (!goldReward.amount || goldReward.amount <= reward.amount) return null;
                    const goToSubscriptions = () => {
                      try { onClose && onClose(); } catch {}
                      // Petite tempo pour laisser le modal se fermer avant la nav
                      setTimeout(() => {
                        try { rootNavigate('Profil'); } catch {}
                      }, 150);
                    };
                    return (
                      <View style={{
                        marginTop: 2,
                        paddingTop: 10,
                        borderTopWidth: 1,
                        borderTopColor: '#A5D6A7',
                        alignItems: 'center',
                        gap: 4,
                      }}>
                        <Text style={{ fontSize: 12, color: '#1B5E20', fontWeight: '700', textAlign: 'center' }}>
                          🚀 {en
                            ? `If you were Gold, you would get ${goldReward.formatted}`
                            : `Si vous étiez Gold, vous récupéreriez ${goldReward.formatted}`}
                        </Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: 6 }}>
                          <Text style={{ fontSize: 10, color: '#558B2F', textAlign: 'center' }}>
                            {en ? 'Gold €16.99/month · no commitment' : 'Gold 16,99€/mois · sans engagement'}
                          </Text>
                          <TouchableOpacity onPress={goToSubscriptions} hitSlop={{ top:6, right:6, bottom:6, left:6 }}>
                            <Text style={{ fontSize: 10, color: '#1B5E20', fontWeight: '700', textDecorationLine: 'underline' }}>
                              {en ? 'See plans →' : 'Voir les abonnements →'}
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    );
                  })()}
                </View>
              );
            })()}

            {/* ANALYSE IA — composant partagé avec WardrobeItemDetailModal (v1.12.48) */}
            <AIMetadataDetails aiMetadata={product.ai_metadata} />

            {/* ═══ Tailles disponibles ═══ */}
            {/* Source : product.sizes agrégé par mergeDuplicateProducts (1.4.9) qui
                fusionne les variantes "Article XS" + "Article S" + "Article M" en
                un seul produit avec sizes: ['XS', 'S', 'M']. Si product.sizes est
                vide, on tente d'autres champs partenaires en fallback. */}
            {(() => {
              // v1.12.987 — Tailles retirées de cet écran : souvent fausses (ex. XS/S/M
              // affichées sur des chaussures qui sont en pointures). Les tailles réelles
              // se consultent sur la page partenaire. Vaut pour TOUS les types d'articles.
              return null;
              /* eslint-disable no-unreachable */
              const candidates = [
                product.sizes,
                product.availableSizes,
                product.available_sizes,
                product.size_options,
                product.variants,
                product.sizeGuide?.sizes,
                product.skus?.map?.(s => s.size),
                product.size ? [product.size] : null, // taille unique en string
              ];
              let sizes = [];
              for (const c of candidates) {
                if (Array.isArray(c) && c.length > 0) {
                  sizes = c
                    .map(s => typeof s === 'string' ? s : (s?.size || s?.name || s?.label || s?.value))
                    .filter(Boolean)
                    .map(s => String(s).trim())
                    .filter(Boolean);
                  if (sizes.length) break;
                }
              }
              // Dédoublonner
              sizes = Array.from(new Set(sizes));

              // Tri intelligent : XXS<XS<S<M<L<XL<XXL puis nombres croissants
              const order = { 'XXS':1,'XS':2,'S':3,'M':4,'L':5,'XL':6,'XXL':7,'XXXL':8,'UNIQUE':99,'TU':99,'U':99 };
              sizes.sort((a, b) => {
                const oa = order[a.toUpperCase()];
                const ob = order[b.toUpperCase()];
                if (oa && ob) return oa - ob;
                if (oa) return -1;
                if (ob) return 1;
                const na = parseFloat(a);
                const nb = parseFloat(b);
                if (!isNaN(na) && !isNaN(nb)) return na - nb;
                return a.localeCompare(b);
              });

              if (sizes.length === 0) {
                return (
                  <View style={{ marginTop: 8 }}>
                    <Text style={{ fontSize: 10, letterSpacing: 2, color: '#999', textTransform: 'uppercase' }}>{en ? 'Size' : 'Taille'}</Text>
                    <Text style={{ fontSize: 12, color: '#999', marginTop: 4, fontStyle: 'italic' }}>
                      {en ? `See available sizes on ${info.name}` : `Voir les tailles disponibles sur ${info.name}`}
                    </Text>
                  </View>
                );
              }

              return (
                <>
                  <Text style={{ fontSize: 10, letterSpacing: 2, color: '#999', textTransform: 'uppercase', marginTop: 8 }}>
                    {en
                      ? `Size (${sizes.length} available)`
                      : `Taille (${sizes.length} disponible${sizes.length > 1 ? 's' : ''})`}
                  </Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <View style={{ flexDirection: 'row', gap: 8, paddingVertical: 4 }}>
                      {sizes.map(sz => (
                        <TouchableOpacity key={sz}
                          style={{
                            paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8,
                            borderWidth: 1.5,
                            borderColor: selectedSize === sz ? '#111' : '#ddd',
                            backgroundColor: selectedSize === sz ? '#111' : '#fff',
                            minWidth: 44,
                            alignItems: 'center',
                          }}
                          onPress={() => setSelectedSize(sz)}>
                          <Text style={{ fontSize: 12, fontWeight: '500', color: selectedSize === sz ? '#fff' : '#555' }}>{sz}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </ScrollView>
                </>
              );
            })()}
          </View>
        </ScrollView>

        {/* Footer : boutons Voir + J'ai acheté */}
        <View style={{ paddingHorizontal: 16, paddingTop: 16, paddingBottom: bottomPadding, gap: 10, borderTopWidth: 1, borderTopColor: '#F0F0F0' }}>
          {hasUrl && (
            <TouchableOpacity
              style={{ backgroundColor: info.color, paddingVertical: 14, borderRadius: 8, alignItems: 'center' }}
              onPress={openInBrowser}>
              <Text style={{ fontSize: 13, fontWeight: '600', color: '#fff' }}>{en ? `View on ${info.name} ↗` : `Voir sur ${info.name} ↗`}</Text>
            </TouchableOpacity>
          )}
          {hasUrl && (
            <TouchableOpacity
              style={{ backgroundColor: '#2E7D32', paddingVertical: 14, borderRadius: 8, alignItems: 'center' }}
              onPress={() => {
                // v1.12.334 — check authentification AVANT d'ouvrir PurchaseConfirmModal
                if (!authUser || (!authUser.accessToken && !authUser.id && !authUser.sub)) {
                  showAppAlert(
                    en ? '🔒 Login required' : '🔒 Connexion requise',
                    en
                      ? 'You need to be logged in to add an item to your wardrobe.\n\nSign in to start building your wardrobe and earn rewards on every purchase.'
                      : "Vous devez être connecté pour ajouter un article à votre dressing.\n\nConnectez-vous pour commencer à construire votre dressing et gagner des récompenses sur vos achats.",
                    [
                      { text: en ? 'Cancel' : 'Annuler', style: 'cancel' },
                      {
                        text: en ? 'Sign in' : 'Se connecter',
                        style: 'default',
                        onPress: () => {
                          try { onClose && onClose(); } catch {}
                          setTimeout(() => { try { rootNavigate('Login'); } catch {} }, 150);
                        },
                      },
                    ],
                  );
                  return;
                }
                onBought && onBought(product);
              }}>
              <Text style={{ fontSize: 13, fontWeight: '600', color: '#fff' }}>{en ? '✓ I bought it → Add to wardrobe' : '✓ J\'ai acheté → Ajouter au dressing'}</Text>
            </TouchableOpacity>
          )}
        </View>
      </SafeAreaView>

      {/* v1.12.545 — Lightbox plein écran pinch-to-zoom (iOS natif via ScrollView maximumZoomScale) */}
      {zoomVisible && imgSrc && (
        <AppModal
          visible={zoomVisible}
          animationType="fade"
          presentationStyle="overFullScreen"
          transparent
          onRequestClose={() => setZoomVisible(false)}>
          <View style={{ flex: 1, backgroundColor: '#000' }}>
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}
              maximumZoomScale={4}
              minimumZoomScale={1}
              bouncesZoom
              showsHorizontalScrollIndicator={false}
              showsVerticalScrollIndicator={false}
              pinchGestureEnabled
              centerContent>
              <Image
                source={{ uri: imgSrc }}
                style={{ width: winW, height: winW / 0.7 }}
                resizeMode="contain" />
            </ScrollView>
            <TouchableOpacity
              onPress={() => setZoomVisible(false)}
              activeOpacity={0.7}
              style={{
                position: 'absolute', top: 50, right: 20,
                width: 44, height: 44, borderRadius: 22,
                backgroundColor: 'rgba(255,255,255,0.15)',
                alignItems: 'center', justifyContent: 'center',
              }}>
              <Text style={{ fontSize: 22, color: '#FFF', fontWeight: '300' }}>✕</Text>
            </TouchableOpacity>
          </View>
        </AppModal>
      )}
    </AppModal>
  );
}
